import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, SearchBar, Pagination, EmptyState, StatusBadge, useList, formatDate, tableClass, theadClass, thClass, trClass, tdClass, tdFirstClass } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import { FormField } from "@/pages/Contacts";
import { UserCog, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "user", is_active: true };

export default function AdminUsers() {
  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters, refetch } = useList("/admin/users");
  const [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setForm(EMPTY); setEditing(null); setDrawer(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, is_active: u.is_active }); setDrawer(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editing.id}`, payload);
        toast.success("User diperbarui.");
      } else {
        await api.post("/admin/users", form);
        toast.success("User dibuat.");
      }
      setDrawer(false); refetch();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Users" subtitle="Kelola akun pelanggan Serabi."
        action={<Button onClick={openCreate} className="bg-[#FFDA1A] hover:bg-[#F5CE00] text-[#111111]" data-testid="admin-create-user-button"><Plus className="w-4 h-4 mr-1.5" /> User Baru</Button>} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari nama, email..." testid="admin-user-search" />
        <Select value={filters.role || "all"} onValueChange={(v) => setFilters({ ...filters, role: v === "all" ? "" : v })}>
          <SelectTrigger className="w-40 bg-white" data-testid="admin-user-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua role</SelectItem><SelectItem value="user">User</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={UserCog} title="Belum ada user" description="Buat akun user baru untuk pelanggan Serabi."
          action={<Button onClick={openCreate} className="bg-[#003399] hover:bg-[#002B80]"><Plus className="w-4 h-4 mr-1.5" /> User Baru</Button>} />
      ) : (
        <div className="bg-white rounded-lg overflow-hidden">
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr><th className={thClass}>Nama</th><th className={thClass}>Email</th><th className={`${thClass} hidden md:table-cell`}>Role</th><th className={thClass}>Status</th><th className={`${thClass} hidden lg:table-cell`}>Dibuat</th></tr>
            </thead>
            <tbody>
              {data.map((u) => (
                <tr key={u.id} onClick={() => openEdit(u)} className={trClass} data-testid={`admin-user-row-${u.id}`}>
                  <td className={`${tdFirstClass} font-medium text-[#111111]`}>{u.name}</td>
                  <td className={tdClass}>{u.email}</td>
                  <td className={`${tdClass} hidden md:table-cell`}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-[4px] text-[11px] font-semibold uppercase tracking-[0.05em] ${u.role === "admin" ? "bg-[#00339912] text-[#003399]" : "bg-[#1111110A] text-[#484848]"}`}>{u.role}</span>
                  </td>
                  <td className={tdClass}><StatusBadge status={u.is_active ? "active" : "inactive"} /></td>
                  <td className={`${tdClass} hidden lg:table-cell text-[#767676] text-xs`}>{formatDate(u.created_at, false)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={drawer} onClose={() => setDrawer(false)} title={editing ? "Edit User" : "User Baru"} description="Akun pelanggan Serabi." testid="admin-user-drawer"
        footer={<><Button variant="outline" onClick={() => setDrawer(false)}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-[#003399] hover:bg-[#002B80]" data-testid="admin-user-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</Button></>}>
        <div className="space-y-4">
          <FormField label="Nama *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-user-name" />
          <FormField label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="admin-user-email" />
          {!editing && <FormField label="Password Awal *" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="admin-user-password" />}
          {editing && <FormField label="Reset Password (opsional)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="admin-user-password" />}
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="admin-user-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="user">User</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between p-3 rounded-[4px] bg-[#F5F5F5]">
            <Label>Akun Aktif</Label>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="admin-user-active" />
          </div>
        </div>
      </RightDrawer>
    </div>
  );
}
